import { NgModule } from '@angular/core';
import { of } from 'rxjs';

import {
  FileSizePipe,
  CardViewTextItemModel,
  CardViewDateItemModel,
  NotificationHistoryComponent,
} from '@alfresco/adf-core';
import {
  LibraryNameColumnComponent,
  LibraryStatusColumnComponent,
  TrashcanNameColumnComponent,
  LibraryRoleColumnComponent,
} from '@alfresco/adf-content-services';
import * as rules from '@alfresco/aca-shared/rules';
import { Node } from '@alfresco/js-api';

import { ExtensionsDataLoaderGuard } from '@alfresco/aca-shared';
import { ViewNodeComponent } from '@adw/projects/aca-content/src/lib/components/toolbar/view-node/view-node.component';
import { ToggleJoinLibraryMenuComponent } from '@adw/projects/aca-content/src/lib/components/toolbar/toggle-join-library/toggle-join-library-menu.component';
import { ToggleJoinLibraryButtonComponent } from '@adw/projects/aca-content/src/lib/components/toolbar/toggle-join-library/toggle-join-library-button.component';
import { ToggleInfoDrawerComponent } from '@adw/projects/aca-content/src/lib/components/toolbar/toggle-info-drawer/toggle-info-drawer.component';
import { ToggleFavoriteComponent } from '@adw/projects/aca-content/src/lib/components/toolbar/toggle-favorite/toggle-favorite.component';
import { ToggleFavoriteLibraryComponent } from '@adw/projects/aca-content/src/lib/components/toolbar/toggle-favorite-library/toggle-favorite-library.component';
import { VersionsTabComponent } from '@adw/projects/aca-content/src/lib/components/info-drawer/versions-tab/versions-tab.component';
import { LibraryMetadataTabComponent } from '@adw/projects/aca-content/src/lib/components/info-drawer/library-metadata-tab/library-metadata-tab.component';
import { CommentsTabComponent } from '@adw/projects/aca-content/src/lib/components/info-drawer/comments-tab/comments-tab.component';
import { CustomNameColumnComponent } from '@adw/projects/aca-content/src/lib/components/dl-custom-components/name-column/name-column.component';
import { ToggleSharedComponent } from '@adw/projects/aca-content/src/lib/components/common/toggle-shared/toggle-shared.component';
import { LocationLinkComponent } from '@adw/projects/aca-content/src/lib/components/common/location-link/location-link.component';
import { LanguagePickerComponent } from '@adw/projects/aca-content/src/lib/components/common/language-picker/language-picker.component';
import { UserMenuComponent } from '@adw/projects/aca-content/src/lib/components/sidenav/user-menu/user-menu.component';
import { WkSiteContainersComponent } from '../components/wk-site-containers/wk-site-containers.component';
import { WkToggleEditOfflineComponent } from '../components/wk-toggle-edit-offline/wk-toggle-edit-offline.component';
import { WkAppLayoutComponent } from '../forked/components/layout/wk-app-layout/wk-app-layout.component';
import { WkCardItemTypeService } from '../services';
import { CoreContentService } from '../services/core-content.service';
import { WkCoreExtensionService } from '../services/wk-core-extensions.service';
import { WkFormRenderingService } from '../services/wk-form-rendering.service';
import { WkUploadWidgetComponent } from '../widgets/wk-upload/wk-upload.widget';
import { WkPeopleWidgetComponent } from '../widgets/wk-people/wk-people.widget';
import { WkInfoDrawerComponent } from '../components/info-drawer/wk-info-drawer.component';
import { WkDateCellComponent } from '../components/wk-date-cell/wk-date-cell.component';
import { WkDateWidgetComponent } from '../components/wk-date-widget/wk-date-widget.component';
import { WkCardViewDateItemComponent } from '../components/wk-card-view-date-item/wk-card-view-date-item.component';
import { MetadataTabComponent } from '../components/info-drawer/metadata-tab/metadata-tab.component';
import { WkCurrentSiteComponent } from '../components/wk-current-site/wk-current-site.component';
import { UserInfoComponent } from '@adw/projects/aca-content/src/lib/components/common/user-info/user-info.component';
import { WkLogoutMenuComponent } from '../components/sidenav/components/wk-logout/wk-logout.component';
import { WkSidenavComponent } from '../components/sidenav/sidenav.component';
import { WKDcoratorsComponent } from '../modules/wk-document-decorator';
import { TagsColumnComponent } from '@adw/projects/aca-content/src/lib/components/dl-custom-components/tags-column/tags-column.component';
import {
  DropdownWidgetComponent,
  FunctionalGroupWidgetComponent,
} from '@alfresco/adf-process-services';
import { WkSearchResultsRowComponent } from '../components/wk-search-results-row/wk-search-results-row.component';
import { WkAuthGuardEcm } from '../guards/wk-auth-ecm.guard';
import { CustomPropertyValidators } from './custom-property-validators';
import * as ces from './rules/app.ces-rules';
import * as customRules from './rules/app.custom-rules';
import { WkCommentsTabComponent } from '../components/info-drawer/wk-comments-tab/wk-comments-tab.component';
import { AlfrescoOfficeExtensionService } from '../msal/services';

@NgModule()
export class ConfigurationModule {
  constructor(
    extensions: WkCoreExtensionService,
    coreContentService: CoreContentService,
    fileSizePipe: FileSizePipe,
    formRenderingService: WkFormRenderingService,
    cardItemTypeService: WkCardItemTypeService,
    private readonly aosService: AlfrescoOfficeExtensionService
  ) {
    this.setComponents(extensions);
    this.setAuthGuards(extensions);
    this.setEvaluators(extensions);

    const callback = (node: Node) => {
      const sizeInBytes = node.content ? node.content.sizeInBytes : '',
        mimeTypeName = node.content ? node.content.mimeTypeName : '',
        tenantId = node.properties ? node.properties['apollo:tenantId'] : '',
        versionLabel = node.properties
          ? node.properties['cm:versionLabel']
          : '';
      return of([
        new CardViewTextItemModel({
          label: 'CORE.METADATA.BASIC.NAME',
          value: node.name,
          key: 'name',
          editable: true,
          validators: [
            CustomPropertyValidators.isAtLeastThreeCharactersLong,
            CustomPropertyValidators.isNotEmpty,
          ],
        }),
        new CardViewTextItemModel({
          label: 'CORE.METADATA.BASIC.VERSION',
          value: versionLabel,
          key: 'properties.cm:versionLabel',
          editable: true,
          validators: [CustomPropertyValidators.isNotEmpty],
        }),
        new CardViewTextItemModel({
          label: 'CORE.METADATA.BASIC.CREATOR',
          value: node.createdByUser.displayName,
          key: 'createdByUser.displayName',
          editable: false,
        }),
        new CardViewDateItemModel({
          label: 'CORE.METADATA.BASIC.CREATED_DATE',
          value: node.createdAt,
          key: 'createdAt',
          editable: false,
          format: 'short',
        }),
        new CardViewDateItemModel({
          label: 'CORE.METADATA.BASIC.MODIFIED_DATE',
          value: node.modifiedAt,
          key: 'modifiedAt',
          editable: false,
          format: 'short',
        }),
        new CardViewTextItemModel({
          label: 'CORE.METADATA.BASIC.MODIFIER',
          value: node.modifiedByUser.displayName,
          key: 'modifiedByUser.displayName',
          editable: false,
        }),
        new CardViewTextItemModel({
          label: 'CORE.METADATA.BASIC.TENANT_IDENTIFIER',
          value: tenantId,
          key: 'properties.apollo:tenantId',
          editable: false,
        }),
        new CardViewTextItemModel({
          label: 'CORE.METADATA.BASIC.MIMETYPE',
          value: mimeTypeName,
          key: 'content.mimeTypeName',
          editable: false,
        }),
        new CardViewTextItemModel({
          label: 'CORE.METADATA.BASIC.SIZE',
          value: sizeInBytes,
          key: 'content.sizeInBytes',
          pipes: [{ pipe: fileSizePipe }],
          editable: false,
        }),
      ]);
    };
    coreContentService.metadataInstance.setBasicProperties(callback);
    formRenderingService.setComponentTypeResolver(
      'upload',
      () => WkUploadWidgetComponent,
      true
    );
    formRenderingService.setComponentTypeResolver(
      'people',
      () => WkPeopleWidgetComponent,
      true
    );
    formRenderingService.setComponentTypeResolver(
      'date',
      () => WkDateWidgetComponent,
      true
    );
    formRenderingService.setComponentTypeResolver(
      'dropdown',
      () => DropdownWidgetComponent,
      true
    );
    formRenderingService.setComponentTypeResolver(
      'functional-group',
      () => FunctionalGroupWidgetComponent,
      true
    );
    cardItemTypeService.setComponentTypeResolver(
      'date',
      () => WkCardViewDateItemComponent,
      true
    );
  }

  private setComponents(extensions: WkCoreExtensionService) {
    extensions.setComponents({
      'app.user': UserInfoComponent,
      'app.user.menu': UserMenuComponent,
      'app.user.menu.site_info': WkCurrentSiteComponent,
      'app.languagePicker': LanguagePickerComponent,
      'app.logout': WkLogoutMenuComponent,
      'app.notification-center': NotificationHistoryComponent,
      'app.layout.main': WkAppLayoutComponent,
      'app.layout.sidenav': WkSidenavComponent,
      'app.toolbar.toggleInfoDrawer': ToggleInfoDrawerComponent,
      'app.toolbar.toggleFavorite': ToggleFavoriteComponent,
      'app.toolbar.toggleFavoriteLibrary': ToggleFavoriteLibraryComponent,
      'app.toolbar.toggleJoinLibrary': ToggleJoinLibraryButtonComponent,
      'app.toolbar.toggleEditOffline': WkToggleEditOfflineComponent,
      'app.toolbar.viewNode': ViewNodeComponent,
      // 'app.toolbar.cardView': DocumentDisplayModeComponent,
      'app.navbar.component.siteContainers': WkSiteContainersComponent,
      'app.menu.toggleJoinLibrary': ToggleJoinLibraryMenuComponent,
      'app.shared-link.toggleSharedLink': ToggleSharedComponent,
      'app.columns.name': CustomNameColumnComponent,
      'app.columns.libraryName': LibraryNameColumnComponent,
      'app.columns.libraryRole': LibraryRoleColumnComponent,
      'app.columns.libraryStatus': LibraryStatusColumnComponent,
      'app.columns.trashcanName': TrashcanNameColumnComponent,
      'app.columns.location': LocationLinkComponent,
      'app.columns.date': WkDateCellComponent,
      'app.columns.tags': TagsColumnComponent,
      'app.components.tabs.citations': WKDcoratorsComponent,
      'app.components.tabs.metadata': MetadataTabComponent,
      'app.components.tabs.library.metadata': LibraryMetadataTabComponent,
      'app.components.tabs.comments': CommentsTabComponent,
      'app.components.tabs.versions': VersionsTabComponent,
      'app.components.tabs.commentstrackchanges': WkCommentsTabComponent,
      'app.components.infodrawer': WkInfoDrawerComponent,
      'app.components.editor': null,
      'app.search.columns.name': WkSearchResultsRowComponent,
    });
  }

  private setAuthGuards(extensions: WkCoreExtensionService) {
    extensions.setAuthGuards({
      'app.auth': WkAuthGuardEcm,
      'app.extensions.dataLoaderGuard': ExtensionsDataLoaderGuard,
    });
  }

  private setEvaluators(extensions: WkCoreExtensionService) {
    extensions.setEvaluators({
      canCopyNode: rules.canCopyNode,
      canToggleJoinLibrary: rules.canToggleJoinLibrary,
      canEditFolder: rules.canEditFolder,
      isTrashcanItemSelected: rules.isTrashcanItemSelected,
      canViewFile: rules.canViewFile,
      canLeaveLibrary: rules.canLeaveLibrary,
      canToggleSharedLink: rules.canToggleSharedLink,
      canShowInfoDrawer: rules.canShowInfoDrawer,
      canManageFileVersions: rules.canManageFileVersions,
      canManagePermissions: rules.canManagePermissions,
      canToggleEditOffline: rules.canToggleEditOffline,
      canToggleFavorite: customRules.canToggleFavorite,
      isLibraryManager: rules.isLibraryManager,
      canEditAspects: rules.canEditAspects,
      isModeEdit: customRules.isModeEdit,

      'app.selection.canDelete': customRules.canDeleteSelection,
      'app.selection.file.canUnlock': rules.canUnlockFile,
      'app.selection.file.canLock': rules.canLockFile,
      'app.selection.canDownload': rules.canDownloadSelection,
      'app.selection.notEmpty': rules.hasSelection,
      'app.selection.canUnshare': rules.canUnshareNodes,
      'app.selection.canAddFavorite': rules.canAddFavorite,
      'app.selection.canRemoveFavorite': rules.canRemoveFavorite,
      'app.selection.first.canUpdate': rules.canUpdateSelectedNode,
      'app.selection.file': rules.hasFileSelected,
      'app.selection.file.canShare': rules.canShareFile,
      'app.selection.file.isShared': rules.isShared,
      'app.selection.file.isLocked': rules.hasLockedFiles,
      'app.selection.file.isLockOwner': rules.isUserWriteLockOwner,
      'app.selection.file.canUploadVersion': rules.canUploadVersion,
      'app.selection.file.canShowCompareAndMergeOption':
        customRules.canShowCompareAndMergeOption,
      'app.selection.library': rules.hasLibrarySelected,
      'app.selection.isPrivateLibrary': rules.isPrivateLibrary,
      'app.selection.hasLibraryRole': rules.hasLibraryRole,
      'app.selection.hasNoLibraryRole': rules.hasNoLibraryRole,
      'app.selection.folder': rules.hasFolderSelected,
      'app.selection.folder.canUpdate': rules.canUpdateSelectedFolder,
      'app.selection.canCheckout': customRules.canStartProcessAndCheckout,
      'app.selection.canCheckin': customRules.canCheckin,
      'app.selection.canDownloadValidationList':
        customRules.canDownloadValidationList,
      'app.selection.canBatchMetadataUpdate':
        customRules.canBatchMetadataUpdate,
      'app.selection.canStartProcess': customRules.canStartProcessAndCheckout,
      'app.selection.canAssignWorkflowStep': customRules.canAssignWorkflowStep,
      'app.selection.canAssignWorkflowStepContextMenu':
        customRules.canAssignWorkflowStepContextMenu,
      'app.selection.canCancelWorkflow': customRules.canCancelWorkflow,
      'app.selection.canReassignWorkflow': customRules.canReassignWorkflow,
      'app.selection.canDownloadSelectedFiles':
        customRules.canDownloadSelectedFiles,
      'app.selection.canEdit': customRules.canEditDocument,
      'app.selection.hasLockedNodes': customRules.hasLockedNodes,
      'app.selection.hasActiveWorkflowNodes':
        customRules.hasActiveWorkflowNodes,

      'ces.selection.file.canEnrich': ces.canEnrich,

      'app.navigation.folder.canCreate': customRules.canCreateFolder,
      'app.navigation.folder.canUpload': rules.canUpload,
      'app.navigation.isTrashcan': rules.isTrashcan,
      'app.navigation.isNotTrashcan': customRules.isNotTrashcan,
      'app.navigation.isLibraries': rules.isLibraries,
      'app.navigation.isLibraryFiles': rules.isLibraryFiles,
      'app.navigation.isPersonalFiles': rules.isPersonalFiles,
      'app.navigation.isNotLibraries': rules.isNotLibraries,
      'app.navigation.isSharedFiles': rules.isSharedFiles,
      'app.navigation.isNotSharedFiles': rules.isNotSharedFiles,
      'app.navigation.isFavorites': rules.isFavorites,
      'app.navigation.isNotFavorites': rules.isNotFavorites,
      'app.navigation.isRecentFiles': rules.isRecentFiles,
      'app.navigation.isNotRecentFiles': rules.isNotRecentFiles,
      'app.navigation.isSearchResults': rules.isSearchResults,
      'app.navigation.isNotSearchResults': rules.isNotSearchResults,
      'app.navigation.isPreview': rules.isPreview,
      'app.navigation.isSharedPreview': rules.isSharedPreview,
      'app.navigation.isFavoritesPreview': rules.isFavoritesPreview,
      'app.navigation.isSharedFileViewer': rules.isSharedFileViewer,
      'app.navigation.isAllDocumentsDashboardLink':
        customRules.isAllDocumentsDashboardLink,
      'app.navigation.canReassignTask': customRules.canReassignTask,
      'repository.isQuickShareEnabled': rules.hasQuickShareEnabled,
      'user.isAdmin': rules.isAdmin,
      'app.canShowLogout': rules.canShowLogout,
      'app.isContentServiceEnabled': rules.isContentServiceEnabled,
      'app.isUploadSupported': rules.isUploadSupported,
      'app.canCreateLibrary': rules.canCreateLibrary,
      'aos.canOpenWithOffice': (context) =>
        this.aosService.isAosPluginEnabled() &&
        customRules.canOpenWithOfficeCustom(context),
    });
  }
}
